import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Form, Modal } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaBoxOpen,
  FaBoxes,
  FaCheck,
  FaClipboardCheck,
  FaEye,
  FaPlus,
  FaRedoAlt,
  FaSearch,
  FaTimes,
  FaTrash,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { isManagementUser, readStoredAdminUser } from './adminAccessControl';
import {
  POS_API_BASE_URL,
  buildApiErrorMessage,
  buildAuthHeaders,
  fetchScopedOutlets,
  formatCurrency,
  formatDateForInput,
  formatShortDateTime,
  getInitialScopedOutlets,
  getItemTypeBadgeClass,
  getItemTypeLabel,
  getSyncStatusBadgeClass,
  getSyncStatusLabel,
  humanizeToken,
  isStockTrackedProduct,
  normalizePagedRecords,
  parseJsonResponse,
  resolveAdminSessionToken,
} from './posBackofficeHelpers';
import './PosCatalogManager.css';
import './PosApprovalManager.css';
import './PosStockAdjustmentApprovalManager.css';

const REQUEST_STATUS_OPTIONS = [
  { value: '', label: 'Semua status' },
  { value: 'pending', label: 'Menunggu approval' },
  { value: 'approved', label: 'Disetujui' },
  { value: 'rejected', label: 'Ditolak' },
];

const REASON_OPTIONS = [
  { value: 'manual_adjustment', label: 'Manual Adjustment' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'lost', label: 'Lost' },
  { value: 'found', label: 'Found' },
  { value: 'expired', label: 'Expired' },
];

const createAdjustmentLine = () => ({
  product_id: '',
  quantity_delta: '',
  note: '',
});

const createInitialForm = () => ({
  adjustment_date: formatDateForInput(new Date()),
  reason: REASON_OPTIONS[0].value,
  note: '',
  lines: [createAdjustmentLine()],
});

const buildLocalIsoDate = value => {
  const parsed = value ? new Date(`${value}T00:00:00`) : new Date();
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }

  const timezoneOffsetMinutes = -parsed.getTimezoneOffset();
  const sign = timezoneOffsetMinutes >= 0 ? '+' : '-';
  const absoluteOffsetMinutes = Math.abs(timezoneOffsetMinutes);
  const offsetHours = String(Math.floor(absoluteOffsetMinutes / 60)).padStart(2, '0');
  const offsetMinutes = String(absoluteOffsetMinutes % 60).padStart(2, '0');
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}T00:00:00${sign}${offsetHours}:${offsetMinutes}`;
};

const escapeHtml = value =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const getRequestStatusVariant = value => {
  switch (String(value || '').toLowerCase()) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'danger';
    default:
      return 'warning';
  }
};

function PosStockAdjustmentApprovalManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = useMemo(() => readStoredAdminUser(), []);
  const canAccess = isManagementUser(currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const initialOutlets = getInitialScopedOutlets(currentUser);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState(initialOutlets);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId || initialOutlets[0]?.id || '');
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [formData, setFormData] = useState(() => createInitialForm());
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingRequest, setSavingRequest] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState({
    start_date: formatDateForInput(new Date(new Date().setDate(new Date().getDate() - 30))),
    end_date: formatDateForInput(new Date()),
  });

  const selectedOutletName = useMemo(
    () => outlets.find(outlet => String(outlet.id) === String(selectedOutlet))?.name || 'Belum dipilih',
    [outlets, selectedOutlet]
  );

  const stockProducts = useMemo(
    () => products.filter(product => isStockTrackedProduct(product)),
    [products]
  );

  const productMap = useMemo(
    () => stockProducts.reduce((accumulator, product) => {
      accumulator[String(product.id)] = product;
      return accumulator;
    }, {}),
    [stockProducts]
  );

  const lines = useMemo(
    () => (Array.isArray(formData.lines) ? formData.lines : []),
    [formData.lines]
  );

  const queueSummary = useMemo(() => (
    requests.reduce((accumulator, request) => {
      const normalizedStatus = String(request?.status || '').toLowerCase();
      accumulator.totalImpact += Number(request?.request_total || 0);
      if (normalizedStatus === 'pending') {
        accumulator.pendingCount += 1;
        accumulator.pendingImpact += Number(request?.request_total || 0);
      } else if (normalizedStatus === 'approved') {
        accumulator.approvedCount += 1;
      } else if (normalizedStatus === 'rejected') {
        accumulator.rejectedCount += 1;
      }
      return accumulator;
    }, {
      pendingCount: 0,
      approvedCount: 0,
      rejectedCount: 0,
      pendingImpact: 0,
      totalImpact: 0,
    })
  ), [requests]);

  const documentSummary = useMemo(() => (
    documents.reduce((accumulator, row) => {
      const items = Array.isArray(row?.items) ? row.items : [];

      accumulator.total += 1;
      accumulator.totalValue += items.reduce((sum, item) => sum + Number(item?.total_cost || 0), 0);
      const normalizedSync = String(row?.accounting_sync_status || '').toLowerCase();
      if (normalizedSync === 'synced') {
        accumulator.synced += 1;
      } else if (normalizedSync === 'failed') {
        accumulator.failed += 1;
      } else {
        accumulator.pending += 1;
      }
      return accumulator;
    }, {
      total: 0,
      synced: 0,
      pending: 0,
      failed: 0,
      totalValue: 0,
    })
  ), [documents]);

  const queueTableStyles = useMemo(() => ({
    rows: {
      style: {
        minHeight: '82px',
      },
    },
    cells: {
      style: {
        paddingTop: '0.62rem',
        paddingBottom: '0.62rem',
      },
    },
  }), []);

  const documentTableStyles = useMemo(() => ({
    rows: {
      style: {
        minHeight: '76px',
      },
    },
    cells: {
      style: {
        paddingTop: '0.58rem',
        paddingBottom: '0.58rem',
      },
    },
  }), []);

  const resetForm = useCallback(() => {
    setFormData(createInitialForm());
  }, []);

  const openRequestModal = useCallback(() => {
    setShowRequestModal(true);
  }, []);

  const closeRequestModal = useCallback(() => {
    if (savingRequest) {
      return;
    }
    setShowRequestModal(false);
  }, [savingRequest]);

  const initializePage = useCallback(async () => {
    try {
      const token = await resolveAdminSessionToken(verifyAndRefreshToken, navigate);
      if (!token) {
        return;
      }

      setAuthToken(token);
      const scopedOutlets = await fetchScopedOutlets({ token, currentUser });
      setOutlets(scopedOutlets);
      setSelectedOutlet(prev => prev || queryOutletId || scopedOutlets[0]?.id || '');
    } catch (error) {
      setErrorMessage(error.message || 'Gagal menyiapkan workspace stock adjustment.');
    }
  }, [currentUser, navigate, queryOutletId, verifyAndRefreshToken]);

  const fetchProducts = useCallback(async token => {
    if (!POS_API_BASE_URL || !selectedOutlet) {
      setProducts([]);
      return;
    }

    const params = new URLSearchParams({
      outlet_id: selectedOutlet,
      page: '1',
      limit: '500',
    });
    const response = await fetch(`${POS_API_BASE_URL}api/products/?${params.toString()}`, {
      headers: buildAuthHeaders(token),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat product POS.'));
    }

    const { records: fetched } = normalizePagedRecords(data);
    setProducts(fetched);
  }, [selectedOutlet]);

  const fetchRequests = useCallback(async token => {
    if (!POS_API_BASE_URL || !selectedOutlet) {
      setRequests([]);
      return;
    }

    const params = new URLSearchParams({
      outlet_id: selectedOutlet,
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
    });
    if (search.trim()) {
      params.set('search', search.trim());
    }
    if (statusFilter) {
      params.set('status', statusFilter);
    }

    const response = await fetch(`${POS_API_BASE_URL}api/stock-adjustments/approval-requests/?${params.toString()}`, {
      headers: buildAuthHeaders(token),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat queue approval stock adjustment.'));
    }

    setRequests(Array.isArray(data?.results) ? data.results : []);
  }, [dateRange.end_date, dateRange.start_date, search, selectedOutlet, statusFilter]);

  const fetchDocuments = useCallback(async token => {
    if (!POS_API_BASE_URL || !selectedOutlet) {
      setDocuments([]);
      return;
    }

    const params = new URLSearchParams({
      outlet_id: selectedOutlet,
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
    });
    const response = await fetch(`${POS_API_BASE_URL}api/stock-adjustments/?${params.toString()}`, {
      headers: buildAuthHeaders(token),
    });
    const data = await parseJsonResponse(response);
    if (!response.ok) {
      throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat dokumen stock adjustment.'));
    }

    setDocuments(Array.isArray(data) ? data : []);
  }, [dateRange.end_date, dateRange.start_date, selectedOutlet]);

  const loadWorkspace = useCallback(async () => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    setLoading(true);
    setErrorMessage('');
    try {
      await Promise.all([
        fetchProducts(authToken),
        fetchRequests(authToken),
        fetchDocuments(authToken),
      ]);
    } catch (error) {
      setErrorMessage(error.message || 'Gagal memuat workspace stock adjustment.');
    } finally {
      setLoading(false);
    }
  }, [authToken, fetchDocuments, fetchProducts, fetchRequests, selectedOutlet]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }
    initializePage();
  }, [canAccess, initializePage, navigate]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }
    loadWorkspace();
  }, [authToken, loadWorkspace, selectedOutlet]);

  const handleFieldChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLineChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map((line, lineIndex) => (
        lineIndex === index
          ? { ...line, [field]: value }
          : line
      )),
    }));
  };

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, createAdjustmentLine()],
    }));
  };

  const removeLine = index => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.length <= 1
        ? [createAdjustmentLine()]
        : prev.lines.filter((_, lineIndex) => lineIndex !== index),
    }));
  };

  const buildPayload = useCallback(() => {
    const items = lines
      .filter(line => Number(line.product_id || 0) > 0 && Number(line.quantity_delta || 0) !== 0)
      .map(line => ({
        product_id: Number(line.product_id),
        quantity_delta: Number(line.quantity_delta),
        note: String(line.note || '').trim(),
      }));

    if (items.length === 0) {
      throw new Error('Tambahkan minimal satu item adjustment dengan delta stok.');
    }

    const seenProducts = new Set();
    items.forEach(item => {
      if (seenProducts.has(item.product_id)) {
        throw new Error('Satu produk hanya boleh muncul sekali dalam satu pengajuan adjustment.');
      }
      seenProducts.add(item.product_id);
    });

    return {
      outlet_id: Number(selectedOutlet),
      adjustment_date: buildLocalIsoDate(formData.adjustment_date),
      reason: String(formData.reason || '').trim(),
      note: String(formData.note || '').trim(),
      items,
    };
  }, [formData.adjustment_date, formData.note, formData.reason, lines, selectedOutlet]);

  const handleSubmit = async event => {
    event.preventDefault();

    setSavingRequest(true);
    try {
      const payload = buildPayload();
      const response = await fetch(`${POS_API_BASE_URL}api/stock-adjustments/`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(payload),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal mengirim pengajuan stock adjustment.'));
      }

      setShowRequestModal(false);
      resetForm();
      await loadWorkspace();
      Swal.fire('Success', 'Pengajuan stock adjustment berhasil dikirim ke approval.', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal mengirim pengajuan stock adjustment.', 'error');
    } finally {
      setSavingRequest(false);
    }
  };

  const handleReviewRequest = useCallback(async (request, action) => {
    const isReject = action === 'reject';
    const result = await Swal.fire({
      title: isReject ? 'Tolak pengajuan adjustment' : 'Setujui pengajuan adjustment',
      input: 'textarea',
      inputLabel: isReject ? 'Catatan penolakan' : 'Catatan approval',
      inputPlaceholder: isReject ? 'Tuliskan alasan penolakan.' : 'Tambahkan catatan jika perlu.',
      inputValue: '',
      inputValidator: value => (isReject && !String(value || '').trim() ? 'Catatan penolakan wajib diisi.' : undefined),
      showCancelButton: true,
      confirmButtonText: isReject ? 'Tolak' : 'Setujui',
      cancelButtonText: 'Batal',
      confirmButtonColor: isReject ? '#dc3545' : '#198754',
    });

    if (!result.isConfirmed) {
      return;
    }

    setReviewing(true);
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/stock-adjustments/approval-requests/${request.id}/${action}`, {
        method: 'POST',
        headers: buildAuthHeaders(authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          note: result.value || '',
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, `Gagal ${isReject ? 'menolak' : 'menyetujui'} pengajuan stock adjustment.`));
      }

      await loadWorkspace();
      Swal.fire('Success', isReject ? 'Pengajuan berhasil ditolak.' : 'Pengajuan berhasil disetujui.', 'success');
    } catch (error) {
      Swal.fire('Error', error.message || `Gagal ${isReject ? 'menolak' : 'menyetujui'} pengajuan stock adjustment.`, 'error');
    } finally {
      setReviewing(false);
    }
  }, [authToken, loadWorkspace]);

  const handleViewRequestDetail = useCallback(async row => {
    const items = Array.isArray(row?.requested_items) ? row.requested_items : [];
    const rowsHtml = items.length
      ? items.map(item => `
          <tr>
            <td>${escapeHtml(item.product_name || `Product #${item.product_id || '-'}`)}</td>
            <td>${escapeHtml(item.quantity_delta > 0 ? `+${item.quantity_delta}` : item.quantity_delta)}</td>
            <td>${escapeHtml(item.stock_before)}</td>
            <td>${escapeHtml(item.stock_after)}</td>
            <td>${escapeHtml(formatCurrency(item.total_cost || 0))}</td>
          </tr>
        `).join('')
      : '<tr><td colspan="5">Belum ada item.</td></tr>';

    const linkedDocument = row?.stock_adjustment;
    await Swal.fire({
      title: `REQ #${row?.id || '-'}`,
      html: `
        <div style="text-align:left">
          <p><strong>Tanggal pengajuan:</strong> ${escapeHtml(formatShortDateTime(row?.requested_at))}</p>
          <p><strong>Tanggal adjustment:</strong> ${escapeHtml(formatShortDateTime(row?.adjustment_date))}</p>
          <p><strong>Status:</strong> ${escapeHtml(humanizeToken(row?.status || 'pending'))}</p>
          <p><strong>Reason:</strong> ${escapeHtml(humanizeToken(row?.reason || '-'))}</p>
          <p><strong>Catatan pengajuan:</strong> ${escapeHtml(row?.request_note || '-')}</p>
          <p><strong>Pengaju:</strong> ${escapeHtml(row?.requested_by_name || '-')} (${escapeHtml(humanizeToken(row?.requested_by_actor_type || 'backoffice'))})</p>
          <p><strong>Reviewer:</strong> ${escapeHtml(row?.reviewed_by_name || '-')}</p>
          <p><strong>Catatan review:</strong> ${escapeHtml(row?.review_note || '-')}</p>
          ${linkedDocument ? `<p><strong>Dokumen hasil:</strong> #${escapeHtml(linkedDocument.id)} • ${escapeHtml(getSyncStatusLabel(linkedDocument.accounting_sync_status))}</p>` : ''}
          <div style="overflow:auto;margin-top:12px">
            <table style="width:100%;border-collapse:collapse">
              <thead>
                <tr>
                  <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Produk</th>
                  <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Delta</th>
                  <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Before</th>
                  <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">After</th>
                  <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Nilai</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </div>
        </div>
      `,
      width: 860,
      confirmButtonText: 'Tutup',
    });
  }, []);

  const handleViewDocument = useCallback(async row => {
    try {
      const response = await fetch(`${POS_API_BASE_URL}api/stock-adjustments/${row.id}`, {
        headers: buildAuthHeaders(authToken),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok) {
        throw new Error(buildApiErrorMessage(response, data, 'Gagal memuat detail stock adjustment.'));
      }

      const detail = data || {};
      const items = Array.isArray(detail.items) ? detail.items : [];
      const rowsHtml = items.length
        ? items.map(item => `
            <tr>
              <td>${escapeHtml(item?.product?.name || `Product #${item?.product_id || '-'}`)}</td>
              <td>${escapeHtml(item.quantity_delta > 0 ? `+${item.quantity_delta}` : item.quantity_delta)}</td>
              <td>${escapeHtml(item.stock_before)}</td>
              <td>${escapeHtml(item.stock_after)}</td>
              <td>${escapeHtml(formatCurrency(item.total_cost || 0))}</td>
            </tr>
          `).join('')
        : '<tr><td colspan="5">Belum ada detail item.</td></tr>';

      await Swal.fire({
        title: `Stock Adjustment #${detail.id || row.id}`,
        html: `
          <div style="text-align:left">
            <p><strong>Tanggal:</strong> ${escapeHtml(formatShortDateTime(detail.adjustment_date || row.adjustment_date))}</p>
            <p><strong>Reason:</strong> ${escapeHtml(humanizeToken(detail.reason || row.reason || '-'))}</p>
            <p><strong>Status:</strong> ${escapeHtml(humanizeToken(detail.status || row.status || 'posted'))}</p>
            <p><strong>Catatan:</strong> ${escapeHtml(detail.note || row.note || '-')}</p>
            <div style="overflow:auto;margin-top:12px">
              <table style="width:100%;border-collapse:collapse">
                <thead>
                  <tr>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Produk</th>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Delta</th>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Before</th>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">After</th>
                    <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #d7dde5">Nilai</th>
                  </tr>
                </thead>
                <tbody>${rowsHtml}</tbody>
              </table>
            </div>
          </div>
        `,
        width: 860,
        confirmButtonText: 'Tutup',
      });
    } catch (error) {
      Swal.fire('Error', error.message || 'Gagal memuat detail stock adjustment.', 'error');
    }
  }, [authToken]);

  const requestColumns = useMemo(() => ([
    {
      name: 'Pengajuan',
      selector: row => row.id,
      sortable: true,
      grow: 1.15,
      minWidth: '220px',
      cell: row => (
        <div className="pos-approval-identity">
          <div className="pos-approval-row-heading">
            <strong>REQ #{row.id || '-'}</strong>
            <span className={`pos-approval-pill pos-approval-pill--${getRequestStatusVariant(row.status)}`}>
              {humanizeToken(row.status || 'pending')}
            </span>
          </div>
          <small>Diajukan {formatShortDateTime(row.requested_at)} • {row.requested_by_name || '-'}</small>
        </div>
      ),
    },
    {
      name: 'Reason',
      selector: row => row.reason,
      sortable: true,
      grow: 1.2,
      minWidth: '230px',
      cell: row => (
        <div className="pos-approval-identity">
          <strong>{humanizeToken(row.reason)}</strong>
          <small>{row.request_note || 'Tanpa catatan pengajuan'}</small>
        </div>
      ),
    },
    {
      name: 'Impact',
      selector: row => row.request_total,
      sortable: true,
      right: true,
      minWidth: '160px',
      maxWidth: '160px',
      cell: row => (
        <div className="pos-approval-identity pos-approval-identity--right">
          <strong>{formatCurrency(row.request_total)}</strong>
          <small>{row.item_count || 0} item</small>
        </div>
      ),
    },
    {
      name: 'Dokumen',
      selector: row => row.stock_adjustment?.id || 0,
      sortable: true,
      grow: 1,
      minWidth: '200px',
      cell: row => (
        <div className="pos-approval-identity">
          <strong>{row.stock_adjustment?.id ? `ADJ #${row.stock_adjustment.id}` : 'Belum terbentuk'}</strong>
          <small>
            {row.stock_adjustment
              ? `${getSyncStatusLabel(row.stock_adjustment.accounting_sync_status)}${row.stock_adjustment.accounting_sync_error ? ` • ${row.stock_adjustment.accounting_sync_error}` : ''}`
              : row.status === 'rejected'
                ? 'Pengajuan ditutup tanpa dokumen'
                : 'Menunggu keputusan approval'}
          </small>
        </div>
      ),
    },
    {
      name: 'Aksi',
      minWidth: '200px',
      maxWidth: '200px',
      cell: row => {
        const isPending = String(row.status || '').toLowerCase() === 'pending';
        return (
          <div className="pos-approval-action-cell pos-approval-action-cell--queue">
            <Button
              size="sm"
              variant="outline-secondary"
              className="pos-approval-action-button pos-approval-action-button--detail"
              onClick={() => handleViewRequestDetail(row)}
            >
              <FaEye /> Detail
            </Button>
            {isPending ? (
              <div className="pos-approval-action-grid">
                <Button size="sm" variant="outline-success" className="pos-approval-action-button" onClick={() => handleReviewRequest(row, 'approve')}>
                  <FaCheck /> Setujui
                </Button>
                <Button size="sm" variant="outline-danger" className="pos-approval-action-button" onClick={() => handleReviewRequest(row, 'reject')}>
                  <FaTimes /> Tolak
                </Button>
              </div>
            ) : null}
          </div>
        );
      },
    },
  ]), [handleReviewRequest, handleViewRequestDetail]);

  const documentColumns = useMemo(() => ([
    {
      name: 'Dokumen',
      selector: row => row.id,
      sortable: true,
      grow: 1.15,
      minWidth: '220px',
      cell: row => (
        <div className="report-center-table__identity">
          <strong>ADJ #{row.id}</strong>
          <small>{formatShortDateTime(row.adjustment_date)}</small>
        </div>
      ),
    },
    {
      name: 'Reason',
      selector: row => row.reason,
      sortable: true,
      grow: 1.2,
      minWidth: '240px',
      cell: row => (
        <div className="report-center-table__identity">
          <strong>{humanizeToken(row.reason || '-')}</strong>
          <small>{row.note || 'Tanpa catatan dokumen'}</small>
        </div>
      ),
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className="pos-catalog-badge pos-catalog-badge--info">
          {humanizeToken(row.status || 'posted')}
        </span>
      ),
    },
    {
      name: 'Sync',
      selector: row => row.accounting_sync_status,
      sortable: true,
      width: '150px',
      cell: row => (
        <span className={getSyncStatusBadgeClass(row.accounting_sync_status)}>
          {getSyncStatusLabel(row.accounting_sync_status)}
        </span>
      ),
    },
    {
      name: 'Aksi',
      width: '120px',
      cell: row => (
        <Button variant="outline-secondary" size="sm" onClick={() => handleViewDocument(row)}>
          <FaEye className="me-2" />
          Detail
        </Button>
      ),
    },
  ]), [handleViewDocument]);

  const renderLineEditor = (line, index) => {
    const selectedProduct = productMap[String(line.product_id)];
    const stockBefore = Number(selectedProduct?.stock || 0);
    const stockAfter = stockBefore + Number(line.quantity_delta || 0);

    return (
      <div key={`adjustment-line-${index}`} className="pos-catalog-document-line">
        <div className="pos-catalog-document-line__grid">
          <Form.Group>
            <Form.Label>Produk</Form.Label>
            <Form.Select
              value={line.product_id}
              onChange={event => handleLineChange(index, 'product_id', event.target.value)}
            >
              <option value="">Pilih produk stok</option>
              {stockProducts.map(product => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Delta</Form.Label>
            <Form.Control
              type="number"
              value={line.quantity_delta}
              onChange={event => handleLineChange(index, 'quantity_delta', event.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Catatan item</Form.Label>
            <Form.Control
              value={line.note}
              onChange={event => handleLineChange(index, 'note', event.target.value)}
              placeholder="Alasan item"
            />
          </Form.Group>
          <Button
            variant="outline-danger"
            type="button"
            onClick={() => removeLine(index)}
          >
            <FaTrash />
          </Button>
        </div>
        <div className="pos-catalog-document-line__meta">
          <span>
            {selectedProduct ? `Snapshot stok ${stockBefore} -> ${stockAfter}` : 'Pilih produk untuk melihat snapshot stok saat ini'}
          </span>
          {selectedProduct ? (
            <span className={getItemTypeBadgeClass(selectedProduct.item_type)}>
              {getItemTypeLabel(selectedProduct.item_type)}
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  const requestFormContent = (
    <div className="d-grid gap-3">
      <div className="pos-stock-adjustment-modal__intro">
        <div className="pos-stock-adjustment-modal__copy">
          <span className="page-eyebrow">Pengajuan baru</span>
          <h2>Ajukan stock adjustment</h2>
          <p>Dokumen final baru terbentuk setelah approval. Snapshot stok di form ini dipakai sebagai referensi awal reviewer.</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <span className="pos-catalog-badge pos-catalog-badge--info">
            {selectedOutletName}
          </span>
          <span className="pos-catalog-badge pos-catalog-badge--success">
            {lines.length} baris item
          </span>
          <span className="pos-catalog-badge pos-catalog-badge--warning">
            Final posting saat approval
          </span>
        </div>
      </div>

      <div className="pos-stock-adjustment-guide">
        <article className="pos-stock-adjustment-guide__item">
          <strong>1. Isi delta stok per produk</strong>
          <span>Gunakan nilai plus untuk penambahan dan minus untuk pengurangan.</span>
        </article>
        <article className="pos-stock-adjustment-guide__item">
          <strong>2. Sertakan alasan dan catatan</strong>
          <span>Catatan yang jelas memudahkan reviewer membaca konteks kehilangan, kerusakan, atau temuan fisik.</span>
        </article>
        <article className="pos-stock-adjustment-guide__item">
          <strong>3. Final posting saat approval</strong>
          <span>Perubahan stok, ledger, dan jurnal accounting hanya berjalan setelah pengajuan disetujui.</span>
        </article>
      </div>

      <div className="pos-catalog-mini-grid">
        <Form.Group>
          <Form.Label>Tanggal adjustment</Form.Label>
          <Form.Control
            type="date"
            name="adjustment_date"
            value={formData.adjustment_date}
            onChange={handleFieldChange}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Reason</Form.Label>
          <Form.Select
            name="reason"
            value={formData.reason}
            onChange={handleFieldChange}
          >
            {REASON_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      <Form.Group>
        <Form.Label>Catatan pengajuan</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="note"
          value={formData.note}
          onChange={handleFieldChange}
          placeholder="Jelaskan konteks selisih stok agar approval lebih cepat."
        />
      </Form.Group>

      <div className="pos-catalog-document-lines">
        {lines.map(renderLineEditor)}
      </div>

      <div className="d-flex justify-content-between flex-wrap gap-2">
        <Button variant="outline-secondary" type="button" onClick={addLine}>
          <FaPlus className="me-2" />
          Tambah item
        </Button>
        <div className="pos-stock-adjustment-inline-note">
          Satu produk hanya boleh satu baris agar delta stok tidak bertabrakan.
        </div>
      </div>
    </div>
  );

  return (
    <Fragment>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaBoxOpen}
          eyebrow="Catalog & Inventory"
          title="Stock Adjustment Approval"
          description="Ajukan koreksi stok, review dampaknya, lalu setujui atau tolak dengan jejak user, catatan, timestamp, dan dokumen final yang tetap rapi."
          breadcrumbs={[
            { label: 'Catalog & Inventory' },
            { label: 'Stock Adjustment' },
          ]}
          meta={(
            <div className="d-flex flex-wrap gap-2">
              <span className="soft-stat">
                <FaBoxOpen /> {selectedOutletName}
              </span>
              <span className="soft-stat">
                <FaClipboardCheck /> {queueSummary.pendingCount} menunggu approval
              </span>
            </div>
          )}
          actions={(
            <div className="d-flex flex-wrap gap-2">
              <Form.Select
                className="page-action-select"
                value={selectedOutlet}
                onChange={event => setSelectedOutlet(event.target.value)}
                disabled={outlets.length <= 1}
              >
                {outlets.map(outlet => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.name}
                  </option>
                ))}
              </Form.Select>
              <Button variant="outline-secondary" onClick={loadWorkspace} disabled={loading || savingRequest || reviewing}>
                <FaRedoAlt className="me-2" />
                Refresh
              </Button>
              <Button variant="primary" onClick={openRequestModal} disabled={!selectedOutlet || !POS_API_BASE_URL || loading || reviewing}>
                <FaPlus className="me-2" />
                Pengajuan baru
              </Button>
            </div>
          )}
        />

        <div className="pos-catalog-shell">
          {!POS_API_BASE_URL ? (
            <Alert variant="danger" className="section-card">
              Endpoint POS belum diatur. Pastikan `REACT_APP_API_PRODUCT_URL` tersedia di konfigurasi `adminfitness`.
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="warning" className="section-card">
              {errorMessage}
            </Alert>
          ) : null}

          <div className="pos-catalog-kpi-grid">
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaClipboardCheck />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Pending approval</p>
                <strong>{queueSummary.pendingCount}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaCheck />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Approved request</p>
                <strong>{queueSummary.approvedCount}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-kpi-card">
              <span className="pos-catalog-kpi-card__icon">
                <FaBoxes />
              </span>
              <div className="pos-catalog-kpi-card__copy">
                <p>Posted document</p>
                <strong>{documentSummary.total}</strong>
              </div>
            </article>
            <article className="surface-card pos-catalog-note-card">
              <span className="page-eyebrow">Outstanding impact</span>
              <h2>{formatCurrency(queueSummary.pendingImpact)}</h2>
              <p>Estimasi nilai persediaan yang masih menunggu keputusan approval pada filter aktif.</p>
            </article>
          </div>

          <section className="surface-card pos-catalog-workspace-card">
            <div className="pos-catalog-workspace-head">
              <div>
                <span className="page-eyebrow">Filter workspace</span>
                <h2>Queue approval dan riwayat adjustment</h2>
                <p>Gunakan outlet, periode, keyword, dan status untuk fokus ke pengajuan yang harus diputuskan atau dokumen yang perlu diaudit.</p>
              </div>
            </div>

            <div className="pos-stock-adjustment-filter-grid">
              <Form.Group>
                <Form.Label>Keyword</Form.Label>
                <div className="toolbar-search-shell">
                  <FaSearch />
                  <input
                    type="search"
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder="Cari reason, pengaju, review note, atau nomor dokumen..."
                  />
                </div>
              </Form.Group>
              <Form.Group>
                <Form.Label>Status request</Form.Label>
                <Form.Select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
                  {REQUEST_STATUS_OPTIONS.map(option => (
                    <option key={option.value || 'all'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Tanggal awal</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.start_date}
                  onChange={event => setDateRange(prev => ({ ...prev, start_date: event.target.value }))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Tanggal akhir</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.end_date}
                  onChange={event => setDateRange(prev => ({ ...prev, end_date: event.target.value }))}
                />
              </Form.Group>
            </div>
          </section>

          <section className="surface-card table-card pos-catalog-table-card">
            <div className="pos-catalog-toolbar">
              <div>
                <span className="page-eyebrow">Queue approval</span>
                <h2 className="mb-1">Pengajuan stock adjustment</h2>
                <p className="text-muted mb-0">Reviewer bisa membaca dampak stok, nilai adjustment, dan membentuk dokumen final langsung dari queue ini.</p>
              </div>

              <div className="pos-catalog-toolbar__meta">
                <span className="pos-catalog-filter-chip">
                  Pending {queueSummary.pendingCount}
                </span>
                <span className="pos-catalog-filter-chip">
                  Rejected {queueSummary.rejectedCount}
                </span>
                <span className="pos-catalog-filter-chip">
                  Total impact {formatCurrency(queueSummary.totalImpact)}
                </span>
              </div>
            </div>

            <AdminDataTable
              columns={requestColumns}
              data={requests}
              progressPending={loading || reviewing}
              pagination
              highlightOnHover
              responsive={false}
              customStyles={queueTableStyles}
              shellClassName="pos-approval-data-table pos-approval-data-table--queue"
              tableMinWidth="1095px"
              emptyTitle="Belum ada pengajuan stock adjustment"
              emptyDescription="Pengajuan yang masuk akan muncul di sini lengkap dengan status approval dan dokumen hasilnya."
            />
          </section>

          <section className="surface-card table-card pos-catalog-table-card">
            <div className="pos-catalog-toolbar">
              <div>
                <span className="page-eyebrow">Dokumen posted</span>
                <h2 className="mb-1">Riwayat stock adjustment final</h2>
                <p className="text-muted mb-0">Audit dokumen adjustment yang sudah lolos approval dan sudah membentuk ledger stok outlet.</p>
              </div>

              <div className="pos-catalog-toolbar__meta">
                <span className="pos-catalog-filter-chip">
                  Synced {documentSummary.synced}
                </span>
                <span className="pos-catalog-filter-chip">
                  Pending sync {documentSummary.pending}
                </span>
                <span className="pos-catalog-filter-chip">
                  Failed sync {documentSummary.failed}
                </span>
              </div>
            </div>

            <AdminDataTable
              columns={documentColumns}
              data={documents}
              progressPending={loading}
              pagination
              highlightOnHover
              responsive={false}
              customStyles={documentTableStyles}
              shellClassName="pos-approval-data-table pos-approval-data-table--source"
              tableMinWidth="880px"
              emptyTitle="Belum ada dokumen stock adjustment"
              emptyDescription="Dokumen final akan muncul setelah pengajuan stock adjustment disetujui."
            />
          </section>
        </div>
      </Container>
      <Modal
        show={showRequestModal}
        onHide={closeRequestModal}
        size="xl"
        centered
        scrollable
        backdrop={savingRequest ? 'static' : true}
        keyboard={!savingRequest}
        dialogClassName="pos-stock-adjustment-modal"
      >
        <Modal.Header closeButton={!savingRequest}>
          <Modal.Title>Pengajuan stock adjustment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form id="stock-adjustment-request-form" onSubmit={handleSubmit}>
            {requestFormContent}
          </Form>
        </Modal.Body>
        <Modal.Footer className="pos-stock-adjustment-modal__footer">
          <Button variant="outline-secondary" type="button" onClick={resetForm} disabled={savingRequest}>
            Reset
          </Button>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" type="button" onClick={closeRequestModal} disabled={savingRequest}>
              Tutup
            </Button>
            <Button variant="primary" type="submit" form="stock-adjustment-request-form" disabled={savingRequest}>
              {savingRequest ? 'Mengirim...' : 'Kirim ke Approval'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
}

export default PosStockAdjustmentApprovalManager;
